import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  customScrollBar,
  mq,
} from '../../../../common/src/utils/styledComponentsUtils';

// Components
import { LoadingSpinner } from 'wg-fe-ui';
import RiskObjectSelection from './RiskObjectSelection';
import GuaranteesSelection from './GuaranteesSelection';
import SessionActiveHeader from './SessionActiveHeader';
import CompanySelection from './CompanySelection';
import AssistantQuestion from '../flow/AssistantQuestion';
import { Redirect, useParams } from 'react-router';

// Redux
import { useSelector } from 'react-redux';

// Types
import { RootState } from '../../store/redux/reducers/rootReducer';
import cogoToast from 'cogo-toast';
import useIsAboveViewportHeight from '../../../../common/src/hooks/useIsAboveViewportHeight';
import QuestionGroupActions from '../../../../flows/src/components/ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import { RiskObject } from '../../types/SelectionTypes';
import ScrollableContentContainer from '../layout/ScrollableContentContainer';
import ContentLayout from '../layout/ContentLayout';

interface Props {
  nextSelection: (arg0: number) => void;
  previousSelection: (arg0: number) => void;
  isLoading: boolean;
  isSessionActive?: boolean;
}

const SelectionContainer: React.FC<React.PropsWithChildren<Props>> = ({
  nextSelection,
  previousSelection,
  isLoading,
  isSessionActive,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const {
    selectionData,
    selectionTypes,
    selectedRiskObjects,
    selectedCompanies,
    selectedGuarantees,
    guarantees,
    companies,
  } = useSelector((state: RootState) => state.selection);
  const page = parseInt(useParams<{ position: string }>().position);
  const above480px = useIsAboveViewportHeight('vga');

  const checkOneOf = () => {
    // Check for oneOf
    const oneOfGuarantees =
      guarantees[selectionTypes[page]?.riskObject || '']
        ?.filter(({ one_of }) => one_of)
        .map(({ tag }) => tag) || [];
    if (oneOfGuarantees.length > 0) {
      if (
        oneOfGuarantees?.some((guarantee) =>
          selectedGuarantees[selectionTypes[page]?.riskObject || ''].includes(
            guarantee,
          ),
        )
      ) {
        return true;
      }
      return false;
    }
    return true;
  };

  const dataForView:
    | {
        title: string;
        component: React.ReactElement;
        canGoNext?: boolean;
        disableMessage?: string;
      }
    | undefined = {
    riskObjects: {
      title: t('What kind of insurance are you looking for?'),
      component: (
        <RiskObjectSelection
          riskObjects={selectionData.riskObjects?.items as RiskObject[]}
        />
      ),
      canGoNext: selectedRiskObjects.length > 0,
    },
    companies: {
      title: t(
        'For what companies do you want to receive offers in this session?',
      ),
      component: (
        <CompanySelection
          companies={companies.filter(({ name }) => name !== 'ag')}
        />
      ),
      canGoNext:
        // If there is a selected company AND the chosen companies support all chosen risk objects
        selectedCompanies.length > 0 &&
        selectedRiskObjects.every((riskObject) =>
          companies
            .filter(({ name }) => selectedCompanies.includes(name))
            .some(({ insurances }) => insurances.includes(riskObject)),
        ),
      disableMessage: t(
        `Choose an insurance company for each chosen risk object`,
      ),
    },
    guarantees: {
      title: t(`What guarantees would you like for the type insurance?`, {
        type: t(selectionTypes[page]?.riskObject?.toLowerCase() || ''),
      }),
      component: (
        <GuaranteesSelection
          guarantees={guarantees[selectionTypes[page]?.riskObject || '']}
          riskObject={selectionTypes[page]?.riskObject}
        />
      ),
      // If there is a selected guarantee AND if a one_of guarantee was selected
      canGoNext:
        selectedGuarantees[selectionTypes[page]?.riskObject || '']?.length >
          0 && checkOneOf(),
    },
  }[selectionTypes[page]?.type];

  const handleOnNextSelection = () => {
    if (dataForView?.disableMessage && !dataForView?.canGoNext) {
      cogoToast.warn(dataForView.disableMessage, { hideAfter: 5 });
    } else if (dataForView?.canGoNext) {
      nextSelection(page);
    }
  };

  if (!dataForView) return <Redirect to="0" />;

  if (isLoading)
    return (
      <Wrapper {...otherProps}>
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      </Wrapper>
    );

  return (
    <>
      <StyledContentLayout
        onBack={() => previousSelection(page)}
        data-test-id="ProdFac_SelectionContainer"
        isSessionActive={isSessionActive}
        above480px={above480px}
        {...otherProps}
      >
        {isSessionActive && <SessionActiveHeader />}
        <ScrollableContentContainer maxW="120.8rem" resetScrollOnChange={page}>
          <StyledAssistantQuestion
            question={dataForView.title}
            isSessionActive={isSessionActive}
          />
          <MiddleContainer>{dataForView.component}</MiddleContainer>
        </ScrollableContentContainer>
        <QuestionGroupActions
          onBack={() => previousSelection(page)}
          onContinue={handleOnNextSelection}
          disabledContinue={!dataForView.canGoNext}
          data-test-id="ADVISORY-CONVO_SELECTION-VIEW_BUTTON-CONTINUE"
        />
      </StyledContentLayout>
    </>
  );
};

const StyledContentLayout = styled(ContentLayout)<{
  isSessionActive?: boolean;
  above480px?: boolean;
}>`
  max-height: 100%;
  overflow: auto;
  ${customScrollBar}
  ${({ isSessionActive, above480px }) =>
    isSessionActive &&
    above480px &&
    css`
      grid-template-rows: auto auto minmax(1px, 1fr) auto;

      ${mq.laptop} {
        grid-template-rows: auto minmax(1px, 1fr) auto;
      }
    `}
`;

const StyledAssistantQuestion = styled(AssistantQuestion)<{
  isSessionActive?: boolean;
}>`
  ${({ isSessionActive }) =>
    isSessionActive &&
    css`
      padding-top: 0;
    `}
`;

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MiddleContainer = styled.div`
  padding-bottom: 2.4rem;
  width: 100%;
  max-width: 100vw;

  ${mq.tablet} {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const Wrapper = styled.div`
  max-width: 125rem;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 2.4rem;
  background-color: white;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;

  ${mq.tablet} {
    padding: 3rem 4rem;
  }

  ${mq.tablet} {
    justify-content: flex-start;
  }

  overflow-y: scroll;
`;

export default SelectionContainer;
